'use client';
import gsap from 'gsap';
import { GsapMagneticProps } from '../common/types';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import useInputType from '../../hooks/useInputType';

const MagneticContainer = styled.div<GsapMagneticProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: ${({ $magneticOverflow }) => $magneticOverflow || 'hidden'};
    z-index: ${({ $magneticZindex }) => ($magneticZindex !== undefined ? $magneticZindex : "auto")};

    cursor: pointer;
    border: ${({ $magneticBorder, theme }) =>
    typeof $magneticBorder === 'function' ? $magneticBorder(theme) : $magneticBorder || 'none'};
    border-radius: ${({$magneticBorderRadius}) => $magneticBorderRadius || 'none' };
    margin: ${({$magneticMargin}) => $magneticMargin || '0'};

    position: ${({ $magneticPosition }) => $magneticPosition || 'static'};
    top: ${({ $magneticTop }) => $magneticTop || 'auto'};
    right: ${({ $magneticRight }) => $magneticRight || 'auto'};
    
    .imgPresent {
    z-index: 1;
    // width: 24px;
    // height: 24px;
    }

    .txtPresent {
    z-index: 1;
    }
`;

const GsapMagnetic: React.FC<GsapMagneticProps> = ({  children, $maxTranslation, ...magneticProps }) => {
    const magneticRef = useRef<HTMLDivElement | null>(null);
    const inputType = useInputType();
    const adjustedMaxTranslation = inputType === 'touch' ? ($maxTranslation ? $maxTranslation / 2 : 5) : $maxTranslation || 30;
    //N/B For the $maxTranslation that has been overriden it is divided by, can devide by any number if there is a need for better results

    useEffect(() => {
        const currentRef = magneticRef.current; // Store the current ref value
        if (!currentRef) return;

        const img = currentRef.querySelector('.imgPresent');
        const text = currentRef.querySelector('.txtPresent');

        const xTo = gsap.quickTo(currentRef, 'x', {duration: 1, ease: 'elastic.out(1, 0.4)'});
        const yTo = gsap.quickTo(currentRef, 'y', {duration: 1, ease: 'elastic.out(1, 0.4)'});

        const xToImg = img ? gsap.quickTo(img, 'x', { duration: 0.10, ease: 'power1.out' }) : null;
        const yToImg = img ? gsap.quickTo(img, 'y', { duration: 0.10, ease: 'power1.out' }) : null;
        const xToText = text ? gsap.quickTo(text, 'x', { duration: 0.10, ease: 'power1.out' }) : null;
        const yToText = text ? gsap.quickTo(text, 'y', { duration: 0.10, ease: 'power1.out' }) : null;

        const mouseMove = (evt: MouseEvent) => {
            if (!currentRef) return;

            const { clientX, clientY } = evt;
            const { width, height, left, top } = currentRef.getBoundingClientRect();
            let x = clientX - (left + width / 2);//in order to center the mouse
            let y = clientY - (top + height / 2);

            // Limit the x and y translations
            x = Math.max(Math.min(x, adjustedMaxTranslation), -adjustedMaxTranslation);
            y = Math.max(Math.min(y, adjustedMaxTranslation), -adjustedMaxTranslation);

            xTo(x);
            yTo(y);

            if (xToImg && yToImg) {
                xToImg(x / 2.4);
                yToImg(y / 2.4);
            }
            if (xToText && yToText) {
                xToText(x / 3.4);
                yToText(y / 3.4);
            }
        };

        const mouseLeave = () => {
            xTo(0);
            yTo(0);
       
            if (xToImg && yToImg) {
                xToImg(0);
                yToImg(0);
            }
            if (xToText && yToText) {
                xToText(0);
                yToText(0);
            }
        };

        currentRef.addEventListener('mousemove', mouseMove);
        currentRef.addEventListener('mouseleave', mouseLeave);

        return () => {
            if (currentRef) {
                currentRef.removeEventListener('mousemove', mouseMove);
                currentRef.removeEventListener('mouseleave', mouseLeave);
            }
        };
    }, [adjustedMaxTranslation]);

    return (
        <MagneticContainer ref={magneticRef} {...magneticProps}>
            {children}
        </MagneticContainer>
    );
}

export default React.memo(GsapMagnetic);
